import { useState, useRef, useEffect, useCallback } from "react";
import { X, Mic, Square, Sparkles } from "lucide-react";
import { streamChat } from "@/lib/streamChat";
import { transcribeAudio, synthesizeSpeech } from "@/lib/voice";
import { toast } from "sonner";

const SILENCE_MS = 1800;
const MAX_RECORD_MS = 20000;
const SILENCE_THRESHOLD = 0.015;

export default function VoiceMode({ sessionId, onTurn, onClose }) {
  const [phase, setPhase] = useState("starting"); // listening | thinking | speaking
  const [level, setLevel] = useState(0);
  const [lastUser, setLastUser] = useState("");
  const [lastAi, setLastAi] = useState("");

  const cancelledRef = useRef(false);
  const streamRef = useRef(null);
  const ctxRef = useRef(null);
  const recorderRef = useRef(null);
  const audioRef = useRef(null);
  const rafRef = useRef(null);

  const cleanupAudio = useCallback(() => {
    rafRef.current && cancelAnimationFrame(rafRef.current);
    recorderRef.current?.state === "recording" && recorderRef.current.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    ctxRef.current?.close().catch(() => {});
    audioRef.current?.pause();
    recorderRef.current = null;
    streamRef.current = null;
    ctxRef.current = null;
    audioRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    cancelledRef.current = true;
    cleanupAudio();
    onClose();
  }, [cleanupAudio, onClose]);

  useEffect(() => () => { cancelledRef.current = true; cleanupAudio(); }, [cleanupAudio]);

  const lastLoudRef = useRef(null);

  // Records one utterance; resolves with audio blob on silence or max duration.
  const recordUtterance = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctxRef.current = ctx;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);

    const chunks = [];
    const rec = new MediaRecorder(stream);
    recorderRef.current = rec;
    rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };

    return new Promise((resolve) => {
      let speechStartedAt = null;
      const startedAt = Date.now();
      rec.onstop = () => resolve(new Blob(chunks, { type: rec.mimeType || "audio/webm" }));
      rec.start(200);

      const tick = () => {
        if (cancelledRef.current) { rec.state === "recording" && rec.stop(); return; }
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / data.length);
        setLevel(Math.min(rms * 6, 1));

        const now = Date.now();
        if (rms > SILENCE_THRESHOLD && !speechStartedAt) speechStartedAt = now;
        const silentFor = speechStartedAt ? now - Math.max(speechStartedAt, lastLoudRef.current || 0) : 0;
        if (rms > SILENCE_THRESHOLD) lastLoudRef.current = now;
        const elapsed = now - startedAt;

        if ((speechStartedAt && silentFor > SILENCE_MS && elapsed > 900) || elapsed > MAX_RECORD_MS) {
          rec.stop();
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    });
  }, []);

  const playAudio = useCallback((url) => new Promise((resolve) => {
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = resolve;
    audio.onerror = resolve;
    audio.play().catch(resolve);
  }), []);

  const loop = useCallback(async () => {
    while (!cancelledRef.current) {
      // 1. Listen
      setPhase("listening");
      lastLoudRef.current = null;
      let blob;
      try {
        blob = await recordUtterance();
      } catch {
        toast.error("Microphone access denied.");
        handleClose();
        return;
      }
      if (cancelledRef.current) return;
      setLevel(0);

      // 2. Transcribe + think
      setPhase("thinking");
      let text = "";
      try { text = (await transcribeAudio(blob) || "").trim(); } catch { /* fall through */ }
      if (cancelledRef.current) return;
      if (!text) continue; // heard nothing — keep listening

      setLastUser(text);
      let reply = "";
      try {
        reply = await streamChat({ sessionId, message: text, onChunk: (full) => setLastAi(full) });
      } catch (e) {
        toast.error(e.message || "The assistant is unavailable right now.");
        continue;
      }
      if (cancelledRef.current) return;
      if (!reply.trim()) continue;
      onTurn(text, reply);

      // 3. Speak
      setPhase("speaking");
      try {
        const url = await synthesizeSpeech(reply);
        if (cancelledRef.current) return;
        await playAudio(url);
      } catch { /* keep looping */ }
    }
  }, [recordUtterance, playAudio, sessionId, onTurn, handleClose]);

  useEffect(() => { loop(); }, [loop]);

  const stopListening = () => {
    if (phase === "listening" && recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  const phaseLabel = { starting: "Starting…", listening: "Listening…", thinking: "Thinking…", speaking: "Speaking…" }[phase];
  const orbScale = phase === "listening" ? 1 + level * 0.55 : phase === "speaking" ? 1.12 : 1;

  return (
    <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-[#05080D]/95 backdrop-blur-xl animate-in fade-in duration-200" data-testid="voice-mode-overlay">
      <button onClick={handleClose} className="absolute top-5 right-5 p-2.5 rounded-full border border-white/12 text-slate-400 hover:text-white hover:bg-white/5 transition-colors" data-testid="voice-mode-close" aria-label="Exit voice mode">
        <X className="h-5 w-5" />
      </button>

      <span className="inline-flex items-center gap-2 rounded-full border border-[#4F8CFF]/25 bg-[#4F8CFF]/8 px-4 py-1.5 mb-10">
        <Sparkles className="h-3.5 w-3.5 text-[#4F8CFF]" />
        <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#8fb4ff]">Vvvoraa Voice</span>
      </span>

      {/* Orb */}
      <button onClick={stopListening} className="relative flex items-center justify-center" data-testid="voice-orb" aria-label="Voice orb">
        <span
          className="absolute rounded-full bg-[#4F8CFF]/20 blur-2xl transition-transform duration-150"
          style={{ width: 220, height: 220, transform: `scale(${orbScale})` }}
        />
        <span
          className={`relative flex h-36 w-36 items-center justify-center rounded-full bg-gradient-to-br from-[#4F8CFF] to-[#7C5CFF] shadow-[0_0_80px_-10px_rgba(79,140,255,0.7)] transition-transform duration-150`}
          style={{ transform: `scale(${orbScale})` }}
        >
          {phase === "speaking" ? (
            <span className="flex items-end gap-1 h-8">
              {[0, 1, 2, 3, 4].map((i) => (
                <span key={i} className="w-1.5 rounded-full bg-white animate-pulse" style={{ height: `${12 + (i % 3) * 10}px`, animationDelay: `${i * 120}ms` }} />
              ))}
            </span>
          ) : phase === "thinking" ? (
            <Sparkles className="h-10 w-10 text-white" />
          ) : (
            <Mic className="h-10 w-10 text-white" />
          )}
        </span>
      </button>

      <p className="mt-10 text-lg font-medium text-white" data-testid="voice-phase">{phaseLabel}</p>
      <p className="mt-2 text-sm text-slate-500">
        {phase === "listening" ? "Speak now — I'll answer when you pause" : ""}
      </p>

      {(lastUser || lastAi) && (
        <div className="mt-8 max-w-md w-full px-6 space-y-3">
          {lastUser && <p className="text-sm text-slate-400 text-center">You: {lastUser}</p>}
          {lastAi && <p className="text-sm text-slate-300 text-center line-clamp-3">Vvvoraa: {lastAi}</p>}
        </div>
      )}

      <button onClick={handleClose} className="mt-10 inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-6 py-2.5 text-sm text-red-400 hover:bg-red-500/20 transition-colors" data-testid="voice-mode-end">
        <Square className="h-3.5 w-3.5" /> End conversation
      </button>
    </div>
  );
}
